import Bugsnag from "@bugsnag/js";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import CartUpsells from "@Components/atoms/cart/CartUpsells";
import Link from "@Components/atoms/Link";
import CartOverviewTile from "@Components/molecules/cart/CartOverviewTile";
import CartAddressContent from "@Components/molecules/cart/CartOverviewTile/CartAddressContent";
import CartDeliveryContent from "@Components/molecules/cart/CartOverviewTile/CartDeliveryContent";
import CheckoutContactStep from "@Components/molecules/cart/CheckoutContactStep";
import CheckoutPaymentBottomSheet from "@Components/molecules/cart/CheckoutPaymentBottomSheet";
import CheckoutPaymentStep from "@Components/molecules/cart/CheckoutPaymentStep";
import PurchaseSummary from "@Components/molecules/cart/PurchaseSummary";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import useCartContext from "@Hooks/useCartContext";
import { ReactComponent as IconShield } from "@Icons/shield.svg";
import { BillingAddress } from "@Types/addresses";
import { Article, PaymentMethod, Step } from "@Types/cart";
import CreditCard from "@Types/creditCard";
import { FormError } from "@Types/error";
import { PriceFormatted } from "@Types/price";
import { errorMessages, generic, getInputError } from "@Utils/cart/payment";
import { getTermsLink } from "@Utils/links";
import { goToCart } from "@Utils/navigation";
import { convertToCheckoutStepProperties, setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  articles: Article[];
  availablePayments: PaymentMethod[];
  billing?: BillingAddress;
  disabled: boolean;
  enableRememberCreditCard: boolean;
  hasCheckedRememberCreditCard: boolean;
  isPaying: boolean;
  onChangeAddressContent: () => void;
  onChangePaymentMethod: (method: PaymentMethod | undefined) => void;
  onCompleteOrder: () => void;
  onDeleteCreditCard: () => void;
  onRememberCreditCard: () => void;
  onUpdatePromoCode: (promoCode: string | undefined) => Promise<void>;
  paymentErrors: FormError[];
  promoCode: string | undefined;
  promoCodeErrorMessage: string | undefined;
  promoTotal: PriceFormatted;
  removePaymentErrors: () => void;
  savedCreditCardIdentifier: string;
  selectedPaymentMethod: PaymentMethod | undefined;
  shippingTotal: PriceFormatted;
  subTotal: PriceFormatted;
  total: PriceFormatted;
}

interface ContainerProps {
  onChangeStep: (step: Step) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => Promise<void>;
  promoCodeErrorMessage: string | undefined;
}

export const CartOverviewStep: FunctionComponent<Props> = ({
  articles,
  availablePayments,
  billing,
  disabled,
  enableRememberCreditCard,
  hasCheckedRememberCreditCard,
  isPaying,
  onChangeAddressContent,
  onChangePaymentMethod,
  onCompleteOrder,
  onDeleteCreditCard,
  onRememberCreditCard,
  onUpdatePromoCode,
  paymentErrors,
  promoCode,
  promoCodeErrorMessage,
  promoTotal,
  removePaymentErrors,
  savedCreditCardIdentifier,
  selectedPaymentMethod,
  shippingTotal,
  subTotal,
  total,
}) => {
  const { isMediumScreen, isSmallScreen } = useBreakpoint();
  const intl = useIntl();
  const purchaseSummaryArticles = useRef<HTMLDivElement>(null);
  const [displayContent, setDisplayContent] = useState<boolean>(false);
  const [displayStickyTotal, setDisplayStickyTotal] = useState<boolean>(false);
  const [displayBottomSheet, setDisplayBottomSheet] = useState<boolean>(false);
  const [editablePaymentStep, setEditablePaymentStep] = useState<boolean>(false);
  const [validatedPaymentMethod, setValidatedPaymentMethod] = useState<boolean>(false);
  const [displayPromoCodeInput, setDisplayPromoCodeInput] = useState<boolean>(false);

  const orderButton = (
    <ButtonSmily
      disabled={
        disabled ||
        isPaying ||
        paymentErrors.length > 0 ||
        (!editablePaymentStep && !isSmallScreen && total.cents > 0) ||
        !selectedPaymentMethod
      }
      onClick={onCompleteOrder}
      size="m"
      type="primary"
    >
      {intl.formatMessage(
        { id: "cheerz_CartOverviewStep_order" },
        {
          value: intl.formatNumber(total.cents / 100, {
            currency: total.currency,
            style: "currency",
          }),
        }
      )}
    </ButtonSmily>
  );

  const handleChangePaymentMethod = (method: PaymentMethod) => {
    selectedPaymentMethod !== method && onChangePaymentMethod(method);
    isSmallScreen && setDisplayBottomSheet(true);
  };

  const handleDismissBottomSheet = () => {
    onChangePaymentMethod(undefined);
    document.body.classList.toggle("noScroll", false);
    setDisplayBottomSheet(false);
  };

  const handleSubmitPromoCode = (code: string | undefined) =>
    onUpdatePromoCode(code)
      .then(() => {
        setDisplayContent(false);
      })
      .catch((message?: string) => {
        Bugsnag.notify(Error(!!message ? message : "onUpdatePromoCode function"));
      });

  const handleValidate = () => {
    removePaymentErrors();
    setValidatedPaymentMethod(true);
    setEditablePaymentStep(true);
    setDisplayBottomSheet(false);
  };

  // TODO: Remove when we will have 2 separate pages for Cart and Checkout
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (purchaseSummaryArticles.current) {
      if (isMediumScreen) {
        setDisplayStickyTotal(false);
        const handleScroll = () => {
          if (purchaseSummaryArticles.current) {
            const detailsHeight = !!promoCode ? 145 : displayPromoCodeInput ? 289 : 137; // Details part - 145: with promo code value, 289: with promo code input, 137: with promo code link
            const summaryHeight = displayContent
              ? 97 + 114 + purchaseSummaryArticles.current.clientHeight + 16 + detailsHeight
              : 97 + 114; // 97: CartBreadcrumb, 114: PurchaseSummary closed, 16: go to cart link
            if (document.documentElement.scrollTop <= summaryHeight - 57) {
              // 57: PurchaseSummary total value height
              setDisplayStickyTotal(false);
            } else {
              setDisplayStickyTotal(true);
            }
          }
        };
        window.addEventListener("scroll", handleScroll);

        return function cleanup() {
          window.removeEventListener("scroll", handleScroll);
        };
      }
    }
  }, [displayContent, displayPromoCodeInput]);

  useEffect(() => {
    if (!!savedCreditCardIdentifier && !validatedPaymentMethod && selectedPaymentMethod === "cc") {
      setEditablePaymentStep(true);
      setValidatedPaymentMethod(true);
    }
  }, [savedCreditCardIdentifier, selectedPaymentMethod, validatedPaymentMethod]);

  useEffect(() => {
    if (paymentErrors.length > 0) {
      setEditablePaymentStep(false);
    }
  }, [paymentErrors.length]);

  return (
    <div className="CartOverviewStep">
      <div className="CartOverviewStep-checkout">
        {!isMediumScreen && (
          <div className="CartOverviewStep-checkout-title">
            {intl.formatMessage({ id: "cheerz_CartOverviewStep_title" })}
          </div>
        )}
        <div className="CartOverviewStep-content-line">
          <CheckoutContactStep />
        </div>
        <div className="CartOverviewStep-content-line">
          <CartAddressContent onClick={onChangeAddressContent} type="delivery" />
        </div>
        {!!billing && billing.firstName !== undefined && (
          <div className="CartOverviewStep-content-line">
            <CartAddressContent onClick={onChangeAddressContent} type="billing" />
          </div>
        )}
        <div className="CartOverviewStep-content-line">
          <CartOverviewTile type="delivery">
            <CartDeliveryContent />
          </CartOverviewTile>
        </div>
        {total.cents > 0 && (
          <CheckoutPaymentStep
            availablePaymentMethods={availablePayments}
            disabled={disabled}
            editable={editablePaymentStep}
            enableRememberCreditCard={enableRememberCreditCard}
            hasCheckedRememberCreditCard={hasCheckedRememberCreditCard}
            onChangePaymentMethod={handleChangePaymentMethod}
            onEdit={() => {
              onChangePaymentMethod(undefined);
              onDeleteCreditCard();
              setEditablePaymentStep(false);
            }}
            onRememberCreditCard={onRememberCreditCard}
            onValidate={handleValidate}
            paymentErrors={paymentErrors}
            removePaymentErrors={removePaymentErrors}
            savedCreditCardIdentifier={savedCreditCardIdentifier}
            selectedPaymentMethod={selectedPaymentMethod}
            validatedPaymentMethod={validatedPaymentMethod}
          />
        )}
        <div className="CartOverviewStep-order">
          {(total.cents <= 0 || validatedPaymentMethod || paymentErrors.length > 0) && (
            <>
              <div className="CartOverviewStep-order-cgu">
                {intl.formatMessage(
                  { id: "cheerz_CartOverviewStep_cgu" },
                  {
                    link: (
                      <a
                        className="CartOverviewStep-order-link"
                        href={getTermsLink(intl.locale)}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {intl.formatMessage({ id: "cheerz_CartOverviewStep_terms" })}
                      </a>
                    ),
                  }
                )}
              </div>
              {orderButton}
              <div className="CartOverviewStep-order-paymentInfos">
                {intl.formatMessage({ id: "cheerz_PurchaseSummary_payment_infos" })}
                <IconShield />
              </div>
            </>
          )}
          {isSmallScreen && !validatedPaymentMethod && paymentErrors.length === 0 && orderButton}
        </div>
      </div>

      <PurchaseSummary
        contentType="articles"
        disabled={disabled}
        displayContent={displayContent}
        displayPromoCodeInput={displayPromoCodeInput}
        estimatedShippingCost={false}
        onClickPromoCodeLink={() => setDisplayPromoCodeInput(true)}
        onClickTitle={() => setDisplayContent(!displayContent)}
        onSubmitPromoCode={handleSubmitPromoCode}
        promoCode={promoCode}
        promoCodeErrorMessage={promoCodeErrorMessage}
        promoTotal={promoTotal}
        shippingTotal={shippingTotal}
        subTotal={subTotal}
        total={total}
      >
        <div
          className="CartOverviewStep-cart"
          style={{
            maxHeight: displayContent ? "1000px" : 0,
          }}
        >
          <Link onClick={() => goToCart(intl.locale)} size="s">
            {intl.formatMessage({
              id: "cheerz_CartOverviewStep_cart_edit",
            })}
          </Link>
          <div className="CartOverviewStep-cart-articles" ref={purchaseSummaryArticles}>
            {articles.map((article) => {
              return (
                <>
                  <div className="CartOverviewStep-cart-article" key={article.id}>
                    {article.product.name}
                    <div className="CartOverviewStep-cart-article-price">
                      {intl.formatNumber(article.total.cents / 100, {
                        currency: article.total.currency,
                        style: "currency",
                      })}
                    </div>
                  </div>
                  <CartUpsells article={article} smallDesign={true} />
                </>
              );
            })}
          </div>
        </div>
      </PurchaseSummary>

      {displayStickyTotal && (
        <div className="CartOverviewStep-stickyTotal">
          <div className="CartOverviewStep-stickyTotal-total">
            {intl.formatMessage({ id: "cheerz_PurchaseSummary_total" })}
            <div className="CartOverviewStep-stickyTotal-total-price">
              {intl.formatNumber(total.cents / 100, {
                currency: total.currency,
                style: "currency",
              })}
            </div>
          </div>
        </div>
      )}

      {isSmallScreen && (
        <CheckoutPaymentBottomSheet
          disabled={disabled}
          display={displayBottomSheet}
          enableRememberCreditCard={enableRememberCreditCard}
          hasCheckedRememberCreditCard={hasCheckedRememberCreditCard}
          onDismiss={handleDismissBottomSheet}
          onRememberCreditCard={onRememberCreditCard}
          onValidate={handleValidate}
          paymentErrors={paymentErrors}
          paymentMethod={selectedPaymentMethod}
        />
      )}
    </div>
  );
};

const CartOverviewStepContainer: FunctionComponent<ContainerProps> = ({
  onChangeStep,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const { addresses, cart, configuration, dispatch, selectedPaymentMethod, ui, user } =
    useCartContext();
  const intl = useIntl();
  const [hasCheckedRememberCreditCard, setHasCheckedRememberCreditCard] = useState<boolean>(false);
  const [savedCreditCard, setSavedCreditCard] = useState<CreditCard | undefined>();
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [alternatePaymentMethods, setAlternatePaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentErrors, setPaymentErrors] = useState<FormError[]>([]);

  useEffect(() => {
    setDataLayerTrack(
      "Checkout Step Viewed",
      convertToCheckoutStepProperties("Overview", 3, cart, selectedPaymentMethod)
    );
    if (cart.lockedDuringPayment) {
      setIsPaying(true);
      if (configuration.apiProviders.payment) {
        configuration.apiProviders.payment.checkPaymentStatus(
          cart.lockedDuringPayment,
          cart.orderNumber,
          handlePaymentError
        );
      }
    } else if (cart.total.cents <= 0) {
      dispatch({
        paymentMethod: "free",
        type: "setSelectedPaymentMethod",
      });
    } else if (configuration.apiProviders.payment) {
      configuration.apiProviders.payment
        .getCustomerSavedCard()
        .then((response: CreditCard | undefined) => {
          setSavedCreditCard(response);
          if (!!response && selectedPaymentMethod === undefined) {
            dispatch({
              paymentMethod: "cc",
              type: "setSelectedPaymentMethod",
            });
          }

          if (configuration.apiProviders.payment) {
            configuration.apiProviders.payment.getGatewayConfigurations(
              addresses.delivery.countryTag,
              cart.currency,
              setAlternatePaymentMethods
            );
          }
        })
        .catch((error: Error) => Bugsnag.notify(error));
    }
  }, []);

  useEffect(() => {
    if (
      selectedPaymentMethod === "cc" &&
      cart.total.cents > 0 &&
      configuration.apiProviders.payment
    ) {
      dispatch({
        disabled: true,
        type: "setDisabledCart",
      });
      configuration.apiProviders.payment
        .setupProcessOutForm()
        .then(() => {
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
        })
        .catch((error: Error) => {
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
          Bugsnag.notify(error);
        });
    }
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (cart.total.cents <= 0) {
      dispatch({
        paymentMethod: "free",
        type: "setSelectedPaymentMethod",
      });
    }
  }, [cart.total.cents]);

  const handleChangePaymentMethod = (method: PaymentMethod | undefined) => {
    method && setPaymentErrors([]);
    dispatch({
      paymentMethod: method,
      type: "setSelectedPaymentMethod",
    });
  };

  const handlePaymentError = (errorCode?: string) => {
    const message = intl.formatMessage({
      id: errorCode && errorMessages[errorCode] ? errorMessages[errorCode] : generic,
    });
    setIsPaying(false);
    setPaymentErrors((oldArray) => [
      ...oldArray,
      { input: errorCode ? getInputError(errorCode) : undefined, message },
    ]);
    setDataLayerTrack("Payment Error Occured", {
      error: errorCode ? errorCode : message,
    });
  };

  const handleComplete = () => {
    if (configuration.apiProviders.payment && selectedPaymentMethod) {
      setDataLayerTrack(
        "Checkout Step Completed",
        convertToCheckoutStepProperties("Overview", 3, cart)
      );
      setIsPaying(true);
      configuration.apiProviders.payment.proceedToPayment(
        cart.orderNumber,
        hasCheckedRememberCreditCard,
        selectedPaymentMethod,
        handlePaymentError,
        addresses.delivery.countryTag,
        cart.currency,
        savedCreditCard
      );
    }
  };

  const handleDeleteCreditCard = () => {
    setSavedCreditCard(undefined);
  };

  const handleChangeAddressContent = () => {
    onChangeStep("address");
  };

  return (
    <>
      <CartOverviewStep
        articles={cart.articles}
        availablePayments={alternatePaymentMethods.concat("cc", "free")}
        billing={addresses.billing}
        disabled={ui.disabled}
        enableRememberCreditCard={!user.isGuest}
        hasCheckedRememberCreditCard={hasCheckedRememberCreditCard}
        isPaying={isPaying}
        onChangeAddressContent={handleChangeAddressContent}
        onChangePaymentMethod={handleChangePaymentMethod}
        onCompleteOrder={handleComplete}
        onDeleteCreditCard={handleDeleteCreditCard}
        onRememberCreditCard={() => setHasCheckedRememberCreditCard(!hasCheckedRememberCreditCard)}
        onUpdatePromoCode={onUpdatePromoCode}
        paymentErrors={paymentErrors}
        promoCode={cart.promoCode}
        promoCodeErrorMessage={promoCodeErrorMessage}
        promoTotal={cart.promoTotal}
        removePaymentErrors={() => setPaymentErrors([])}
        savedCreditCardIdentifier={savedCreditCard?.identifier ? savedCreditCard?.identifier : ""}
        selectedPaymentMethod={selectedPaymentMethod}
        shippingTotal={cart.shippingTotal}
        subTotal={cart.subTotal}
        total={cart.total}
      />
      <div id="alternative-process-out-container" />
    </>
  );
};

export default CartOverviewStepContainer;
